import { supabaseClient } from './supabase';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { PolicyScenario, fetchBaselineGHGData } from './policyImpactService';
import { 
  getCachedBaseline, 
  setCachedBaseline, 
  BaselineProjections,
  getOrCreateCalculationPromise
} from './policyBaselineCache';
import { getVMTDataForGHG } from './vmtDataService';
import { calculateTotalElectricityConsumption, calculateTotalNaturalGasConsumption, getBuildingsEnergyData } from './buildingsEnergyDataService';
import { fetchCarbonIntensityData } from './carbonIntensityService';

export interface TimeSeriesEmissionData {
  year: number;
  lightDutyEmissions: number;
  heavyDutyEmissions: number;
  naturalGasEmissions: number;
  electricityEmissions: number;
  wasteEmissions: number;
  offroadEmissions: number;
  totalEmissions: number;
}

export interface PolicyTimeSeriesResult {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
  location: string;
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

type EmfacForecastRow = {
  year: number;
  forecasting_ratio?: number;
  ghg_per_mile?: number;
  total_vmt?: number;
};

function buildEmfacInterpolator(rows: EmfacForecastRow[]) {
  const byYear = new Map<number, EmfacForecastRow>();
  const years = rows
    .map((r) => Number(r.year))
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  rows.forEach((r) => {
    const y = Number(r.year);
    if (!Number.isFinite(y)) return;
    byYear.set(y, { ...r, year: y });
  });

  const uniqYears = Array.from(new Set(years)).sort((a, b) => a - b);

  const findBounds = (year: number): { lo?: number; hi?: number } => {
    if (uniqYears.length === 0) return {};
    if (byYear.has(year)) return { lo: year, hi: year };
    let lo: number | undefined;
    let hi: number | undefined;
    for (let i = 0; i < uniqYears.length; i++) {
      const y = uniqYears[i];
      if (y < year) lo = y;
      if (y > year) {
        hi = y;
        break;
      }
    }
    return { lo, hi };
  };

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const interpolateNumber = (year: number, key: keyof EmfacForecastRow): number | undefined => {
    const exact = byYear.get(year);
    const exactVal = exact?.[key];
    if (typeof exactVal === 'number' && Number.isFinite(exactVal)) return exactVal;

    const { lo, hi } = findBounds(year);
    const loVal = lo !== undefined ? byYear.get(lo)?.[key] : undefined;
    const hiVal = hi !== undefined ? byYear.get(hi)?.[key] : undefined;

    // Interpolate if we have both sides
    if (
      lo !== undefined &&
      hi !== undefined &&
      lo !== hi &&
      typeof loVal === 'number' &&
      Number.isFinite(loVal) &&
      typeof hiVal === 'number' &&
      Number.isFinite(hiVal)
    ) {
      const t = (year - lo) / (hi - lo);
      return lerp(loVal, hiVal, t);
    }

    // Extrapolate using last two points (forward)
    if (lo !== undefined && hi === undefined && uniqYears.length >= 2) {
      const last = uniqYears[uniqYears.length - 1];
      const prev = uniqYears[uniqYears.length - 2];
      const lastVal = byYear.get(last)?.[key];
      const prevVal = byYear.get(prev)?.[key];
      if (
        typeof lastVal === 'number' &&
        Number.isFinite(lastVal) &&
        typeof prevVal === 'number' &&
        Number.isFinite(prevVal) &&
        last !== prev
      ) {
        const slopePerYear = (lastVal - prevVal) / (last - prev);
        return lastVal + slopePerYear * (year - last);
      }
    }

    // Extrapolate using first two points (backward)
    if (hi !== undefined && lo === undefined && uniqYears.length >= 2) {
      const first = uniqYears[0];
      const next = uniqYears[1];
      const firstVal = byYear.get(first)?.[key];
      const nextVal = byYear.get(next)?.[key];
      if (
        typeof firstVal === 'number' &&
        Number.isFinite(firstVal) &&
        typeof nextVal === 'number' &&
        Number.isFinite(nextVal) &&
        next !== first
      ) {
        const slopePerYear = (nextVal - firstVal) / (next - first);
        return firstVal - slopePerYear * (first - year);
      }
    }

    return undefined;
  };

  const getRow = (year: number): EmfacForecastRow | undefined => {
    const exact = byYear.get(year);
    if (exact) return exact;
    if (uniqYears.length === 0) return undefined;
    // Build a synthetic row with interpolated values when missing
    const forecasting_ratio = interpolateNumber(year, 'forecasting_ratio');
    const ghg_per_mile = interpolateNumber(year, 'ghg_per_mile');
    const total_vmt = interpolateNumber(year, 'total_vmt');
    if (
      typeof forecasting_ratio !== 'number' &&
      typeof ghg_per_mile !== 'number' &&
      typeof total_vmt !== 'number'
    ) {
      return undefined;
    }
    return { year, forecasting_ratio, ghg_per_mile, total_vmt };
  };

  return { getRow, years: uniqYears };
}

async function fetchCountyPopulationByYear(location: string): Promise<Record<number, number>> {
  try {
    await supabaseLocationService.initialize();
  } catch {
    // ignore; some callers already initialized
  }

  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '').trim();
  const variations = Array.from(
    new Set([resolvedCounty, baseCountyName, `${baseCountyName} County`].filter(Boolean))
  );

  for (const loc of variations) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', loc)
      .order('year', { ascending: true });

    if (!error && data && data.length > 0) {
      const out: Record<number, number> = {};
      data.forEach((r: any) => {
        if (r?.year && r?.population) out[Number(r.year)] = Number(r.population);
      });
      return out;
    }
  }

  return {};
}

function fillAndExtrapolatePopulation(popByYear: Record<number, number>, startYear: number, endYear: number): Record<number, number> {
  const yearsAvail = Object.keys(popByYear).map((y) => parseInt(y, 10)).filter(Number.isFinite).sort((a, b) => a - b);
  if (yearsAvail.length === 0) return {};

  const lastYear = yearsAvail[yearsAvail.length - 1];
  const prevYear = yearsAvail[yearsAvail.length - 2] ?? lastYear - 1;
  const growthPerYear =
    lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;

  const filled: Record<number, number> = { ...popByYear };

  // Forward extrapolation
  for (let y = lastYear + 1; y <= endYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[lastYear] + growthPerYear * (y - lastYear)));
  }

  // Backfill earlier years (flat at earliest)
  const firstYear = yearsAvail[0];
  for (let y = startYear; y < firstYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[firstYear]));
  }

  return filled;
}

/**
 * Fetch EMFAC county filtered data for VMT projection using forecasting_ratio
 * Uses the same approach as GHG Inventory to get ghg_per_mile values
 * @param location - Location name
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 */
async function fetchEMFACForecastingData(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
  // Resolve county for the city to query EMFAC at the county level
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  console.log(`🔍 fetchEMFACForecastingData: location="${location}", resolvedCounty="${resolvedCounty}", emfacLocation="${emfacLocation}"`);
  
  // Select table based on EMFAC version
  const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
  
  // Try common naming variants
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`,
    resolvedCounty
  ];
  
  for (const loc of locationVariations) {
    console.log(`🔍 Policy Tool: Trying EMFAC${emfacVersion} county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (error) {
      console.log(`⚠️ Policy Tool: EMFAC${emfacVersion} county filtered query error for`, loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log(`✅ Policy Tool: EMFAC${emfacVersion} county filtered data found for`, loc, `(${data.length} years)`);
      // Log key values for debugging
      const year2023 = data.find(d => d.year === 2023);
      const year2024 = data.find(d => d.year === 2024);
      if (year2023) console.log(`  2023: ghg_per_mile=${year2023.ghg_per_mile}, forecasting_ratio=${year2023.forecasting_ratio}`);
      if (year2024) console.log(`  2024: ghg_per_mile=${year2024.ghg_per_mile}, forecasting_ratio=${year2024.forecasting_ratio}`);
      return data;
    }
  }
  
  console.log(`Policy Tool: No EMFAC${emfacVersion} county filtered data found for any location variation:`, locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Policy Tool: Trying California EMFAC${emfacVersion} county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from(tableName)
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`Policy Tool: California EMFAC${emfacVersion} county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      console.log(`Policy Tool: Using California EMFAC${emfacVersion} county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  console.log(`Policy Tool: No EMFAC${emfacVersion} data available`);
  return [];
}

/**
 * Calculate historical emissions (2005-2023)
 * IMPORTANT: Historical data is ALWAYS pulled directly from the sector-based GHG inventory chart.
 * The EMFAC version selector only affects projections (2024+), not historical data.
 * This ensures historical values are identical to what's shown in the GHG inventory chart.
 */
async function calculateHistoricalEmissions(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get the GHG inventory data - this is the source of truth for all historical emissions
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract year-specific historical data from the inventory result
    const historicalData: TimeSeriesEmissionData[] = [];
    
    // Get the chart data which contains year-specific emissions
    const chartData = inventoryData.chartData;
    const years = chartData.labels.map((year: any) => parseInt(year));
    
    console.log('Policy Tool: Available years in GHG inventory:', years);
    console.log('Policy Tool: Using GHG inventory chart data directly (EMFAC version does not affect historical data)');
    
    // Extract datasets for ALL categories from GHG inventory chart
    // This ensures historical data matches exactly what's shown in the GHG inventory chart
    const lightDutyDataset = chartData.datasets.find((d: any) => d.label === 'Light Duty Vehicles');
    const heavyDutyDataset = chartData.datasets.find((d: any) => d.label === 'Heavy Duty Vehicles');
    const naturalGasResDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Residential');
    const naturalGasComDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Commercial');
    const electricityResDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Residential');
    const electricityComDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Commercial');
    const wasteDataset = chartData.datasets.find((d: any) => d.label === 'Waste');
    const offroadDataset = chartData.datasets.find((d: any) => d.label === 'Offroad');

    // Process all years from 2005 to 2023
    for (let year = 2005; year <= 2023; year++) {
      const yearIndex = years.indexOf(year);
      
      if (yearIndex !== -1) {
        // Use GHG Inventory chart data directly for ALL sources (including LDV)
        // This ensures historical data is identical to the GHG inventory chart
        const lightDutyEmissions = lightDutyDataset?.data[yearIndex] || 0;
        const heavyDutyEmissions = heavyDutyDataset?.data[yearIndex] || 0;
        
        // Combine residential and commercial natural gas emissions
        const naturalGasRes = naturalGasResDataset?.data[yearIndex] || 0;
        const naturalGasCom = naturalGasComDataset?.data[yearIndex] || 0;
        const naturalGasEmissions = naturalGasRes + naturalGasCom;
        
        // Combine residential and commercial electricity emissions
        const electricityRes = electricityResDataset?.data[yearIndex] || 0;
        const electricityCom = electricityComDataset?.data[yearIndex] || 0;
        const electricityEmissions = electricityRes + electricityCom;

        const wasteEmissions = wasteDataset?.data?.[yearIndex] || 0;
        const offroadEmissions = offroadDataset?.data?.[yearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round(heavyDutyEmissions),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          wasteEmissions: Math.round(wasteEmissions),
          offroadEmissions: Math.round(offroadEmissions),
          totalEmissions: Math.round(lightDutyEmissions + heavyDutyEmissions + naturalGasEmissions + electricityEmissions + wasteEmissions + offroadEmissions)
        });
        
        if (year >= 2020 || year % 5 === 0) {
          console.log(`Policy Tool: Historical ${year} (from GHG Inventory): LD=${lightDutyEmissions}, HD=${heavyDutyEmissions}, NG=${naturalGasEmissions}, EL=${electricityEmissions}`);
        }
      } else {
        // Fallback: use latest year's data if specific year not available
        const latestYearIndex = years.length - 1;
        
        // Use latest year's data from GHG Inventory for ALL sources
        const latestLightDuty = lightDutyDataset?.data[latestYearIndex] || 0;
        const latestHeavyDuty = heavyDutyDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasRes = naturalGasResDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasCom = naturalGasComDataset?.data[latestYearIndex] || 0;
        const latestElectricityRes = electricityResDataset?.data[latestYearIndex] || 0;
        const latestElectricityCom = electricityComDataset?.data[latestYearIndex] || 0;
        const latestWaste = wasteDataset?.data?.[latestYearIndex] || 0;
        const latestOffroad = offroadDataset?.data?.[latestYearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(latestLightDuty),
          heavyDutyEmissions: Math.round(latestHeavyDuty),
          naturalGasEmissions: Math.round(latestNaturalGasRes + latestNaturalGasCom),
          electricityEmissions: Math.round(latestElectricityRes + latestElectricityCom),
          wasteEmissions: Math.round(latestWaste),
          offroadEmissions: Math.round(latestOffroad),
          totalEmissions: Math.round(latestLightDuty + latestHeavyDuty + latestNaturalGasRes + latestNaturalGasCom + latestElectricityRes + latestElectricityCom + latestWaste + latestOffroad)
        });
      }
    }
    
    console.log('Policy Tool: Historical emissions calculated from GHG inventory chart:', historicalData);
    return historicalData;
  } catch (error) {
    console.error('Error calculating historical emissions:', error);
    return [];
  }
}

/**
 * Project future emissions (2023-2045) using EMFAC forecasting data
 */
async function calculateProjectedEmissions(
  location: string, 
  historicalData: TimeSeriesEmissionData[],
  vmtReductionPercent: number = 0,
  evAdoptionPercent: number = 0,
  currentEVPercent: number = 0,
  scenario?: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025'
): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get EMFAC forecasting data (county-level)
    const emfacData = await fetchEMFACForecastingData(location, emfacVersion);
    const emfacInterp = buildEmfacInterpolator((emfacData || []) as EmfacForecastRow[]);
    
    // Get VMT data from Transportation page (includes user customizations) - same as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      console.warn('Could not get geofips for VMT data:', error);
    }

    // Buildings page inputs for projections:
    // - electricity consumption (kWh) by year
    // - natural gas consumption (therms) by year
    // - electricity carbon intensity (gCO2e/kWh) by year
    const buildingsEnergyData = await getBuildingsEnergyData(location);
    let electricityIntensityGPerKwhByYear: Record<number, number> = {};
    try {
      const geofipsStr = geofips ? String(geofips) : null;
      if (geofipsStr) {
        const ci = await fetchCarbonIntensityData(geofipsStr);
        const years = (ci.chartData?.labels || []) as any[];
        const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
        years.forEach((y: any, i: number) => {
          const yr = Number(y);
          const v = Number(data[i]);
          if (Number.isFinite(yr) && Number.isFinite(v) && v > 0) electricityIntensityGPerKwhByYear[yr] = v;
        });
      }
    } catch (e) {
      console.warn('Could not fetch carbon intensity series; electricity emissions will use fallback intensity.', e);
      electricityIntensityGPerKwhByYear = {};
    }
    const g2023 = electricityIntensityGPerKwhByYear[2023] || 300; // gCO2e/kWh fallback
    const cleanElectricityYear = 2045; // SB 100 default for baseline projections
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
    console.log('VMT data for projection:', vmtData);
    
    // Get the latest VMT value (2023) as baseline
    const latestVmtData = vmtData.find(d => d.year === 2023);
    const baselineVmt = latestVmtData?.vmt || 0;
    
    console.log('Baseline VMT (2023):', baselineVmt);
    
    // Calculate 2023 city-specific emission factor from historical GHG inventory data
    // This ensures continuity: 2023 uses city-specific EF (from GHG inventory), 2024+ uses county EF * scaling_factor
    // Formula: EF_city_2023 = emissions_2023 / VMT_2023 (from GHG inventory)
    let cityEmissionFactor2023: number | null = null;
    let countyEmissionFactor2023: number | null = null;
    let emissionFactorScalingFactor: number = 1.0;
    
    try {
      // Get 2023 historical emissions and VMT to calculate city-specific emission factor
      const historical2023 = historicalData.find(d => d.year === 2023);
      const ldv2023 = historical2023?.lightDutyEmissions || 0;
      
      if (ldv2023 > 0 && baselineVmt > 0) {
        // Calculate city-specific emission factor from GHG inventory: emissions / VMT
        cityEmissionFactor2023 = ldv2023 / baselineVmt; // tons CO2 per mile
        console.log(`🔍 City-specific EF 2023 (from GHG inventory): ${cityEmissionFactor2023.toFixed(8)} (emissions=${ldv2023.toFixed(2)} / VMT=${baselineVmt.toFixed(0)})`);
      }
      
      // Get county-level emission factor for 2023 from EMFAC data
      const emfac2023Data = emfacInterp.getRow(2023) || emfacData.find(d => d.year === 2023);
      if (emfac2023Data && emfac2023Data.ghg_per_mile) {
        countyEmissionFactor2023 = Number(emfac2023Data.ghg_per_mile);
        console.log(`🔍 County-level EF 2023 (from emfac${emfacVersion}_county_filtered): ${countyEmissionFactor2023.toFixed(8)}`);
      }
      
      // Calculate scaling factor: EF_city_2023 / EF_county_2023
      // This scaling factor will be applied to all future years to maintain city-specific adjustment
      // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
      if (cityEmissionFactor2023 && countyEmissionFactor2023 && countyEmissionFactor2023 > 0) {
        emissionFactorScalingFactor = cityEmissionFactor2023 / countyEmissionFactor2023;
        console.log(`🔍 Emission Factor Scaling Factor: ${emissionFactorScalingFactor.toFixed(6)} (city_2023=${cityEmissionFactor2023.toFixed(8)} / county_2023=${countyEmissionFactor2023.toFixed(8)})`);
      } else {
        console.warn(`⚠️ Could not calculate scaling factor: city_2023=${cityEmissionFactor2023}, county_2023=${countyEmissionFactor2023}`);
      }
    } catch (error) {
      console.warn('Could not calculate city-specific emission factor for scaling:', error);
    }
    
    const projectedData: TimeSeriesEmissionData[] = [];
    const historical2023 = historicalData.find(d => d.year === 2023);
    
    // Calculate fallback average emission factor from available EMFAC data
    const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
    const avgEmissionFactor = validEmissionFactors.length > 0 
      ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
      : 0.0008; // Fallback emission factor (tons CO2 per mile)
    
    console.log('Policy Tool: Fallback average emission factor:', avgEmissionFactor, 'tons CO2 per mile');
    console.log('Policy Tool: Available emission factors:', validEmissionFactors.map(d => `${d.year}: ${d.ghg_per_mile}`));
    
    // If we have an EMFAC emission factor for 2023, recompute LDV 2023 with it
    // Pre-fetch baseline data and calculate grid intensity for EV policy
    let baseline: any = null;
    let gridIntensity: number | null = null;
    const CONSTANTS = {
      BASELINE_GRID_INTENSITY: 0.2,
      ANNUAL_SOLAR_HOURS: 2000,
      SOLAR_DISPLACEMENT_FACTOR: 0.85,
      EV_KWH_PER_MILE: 0.3,
      ICE_IMPROVEMENT_FACTOR: 0.95,
    };
    
    // Calculate grid intensity for EV adoption (needed for EV emission factor calculation)
    // Always calculate grid intensity if there's any EV adoption (baseline BAU ramps from 6.01% to 69.27%)
    if (evAdoptionPercent > 0 || currentEVPercent > 0) {
      try {
        baseline = await fetchBaselineGHGData(location, emfacVersion);
        const solarMult = scenario?.solarCapacityMultiplier || 1;
        const solarCapacityKw = baseline.solarCapacityKw * solarMult;
        const totalElectricityConsumption = baseline.population * 7000;
        const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;
        const solarPenetration = totalElectricityConsumption > 0 ? solarGeneration / totalElectricityConsumption : 0;
        gridIntensity = CONSTANTS.BASELINE_GRID_INTENSITY * (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));
      } catch (e) {
        console.warn('Could not fetch baseline data for EV calculation:', e);
      }
    }

    // Population scaling (county) for projecting Waste / Natural Gas / Electricity
    const countyPopRaw = await fetchCountyPopulationByYear(location);
    const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);
    const basePop = countyPopByYear[2023] || 1;

    // Track cumulative VMT as we apply ratios sequentially
    // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1) (from emfac2025_county_filtered)
    // So to get VMT_2024: VMT_2024 = VMT_2023 * forecasting_ratio_2024
    // To get VMT_2025: VMT_2025 = VMT_2024 * forecasting_ratio_2025
    let currentVmt = baselineVmt;
    
    // IMPORTANT: Do NOT recalculate 2023 here - use historical data from GHG Inventory chart directly
    // The GHG Inventory chart already uses VMT data from the VMT chart (Transportation page)
    // and respects user customizations (backcasting method, data table edits, etc.)
    // Starting from 2024, we project forward using EMFAC forecasting ratios
    for (let year = 2024; year <= 2045; year++) {
      
      // For years 2024+, apply forecasting ratio for THIS year
      // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
      // So to get VMT for year Y: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
      
      // Find EMFAC data for this year (for emission factor and forecasting ratio)
      const emfacYearData = emfacInterp.getRow(year) || emfacData.find(d => d.year === year);
      
      // Get 2023 historical data for continuity
      // For 2024, verify the calculation matches the expected formula:
      // emissions_2024 = VMT_2023 * forecasting_ratio_2024 * emission_factor_2024
      // This should be < emissions_2023 if both ratio and emission factor decrease
      
      // For projections (2024+), ALWAYS use forecasting ratios from emfac2025_county_filtered
      // Historical data is only to 2023. Everything after 2023 is a projection using ratios.
      // Never use actual VMT values for projections - they may have discontinuities.
      // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
      // Applied as: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
      const emfacYearRatio = emfacYearData?.forecasting_ratio;
      let projectedVmt: number;
      
      if (emfacYearRatio && emfacYearRatio > 0) {
        // Always use forecasting ratio for projections: VMT_year = VMT_(year-1) * forecasting_ratio_year
        const prevVmt = currentVmt;
        projectedVmt = currentVmt * emfacYearRatio;
        currentVmt = projectedVmt; // Update for next iteration
        
        // Debug VMT calculation for 2024-2027
        if (year >= 2024 && year <= 2027) {
          console.log(`🔍 VMT Calc ${year}: Using forecasting ratio, prevVmt=${prevVmt.toFixed(0)}, ratio_${year}=${emfacYearRatio.toFixed(6)}, projectedVmt=${projectedVmt.toFixed(0)}`);
        }
      } else {
        // No forecasting ratio available, keep previous VMT (should not happen for 2024+)
        console.warn(`⚠️ No forecasting ratio available for year ${year}, keeping previous VMT`);
        projectedVmt = currentVmt;
      }
      
      // Use year-specific emission factor from EMFAC data (county-level), or fallback to average
      const yearEmissionFactorCounty = emfacYearData?.ghg_per_mile || avgEmissionFactor;
      
      // Apply scaling factor to maintain continuity with 2023 city-specific emission factor
      // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
      // This ensures that the city-specific adjustment from 2023 is maintained for all projection years
      // while using county-level emission factor trends for the projection
      const adjustedEmissionFactor = yearEmissionFactorCounty * emissionFactorScalingFactor;
      
      if (year === 2024) {
        console.log(`🔍 2024 EF Adjustment: EF_2024_county=${yearEmissionFactorCounty.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}, EF_2024_adjusted=${adjustedEmissionFactor.toFixed(8)}`);
        if (cityEmissionFactor2023 && countyEmissionFactor2023) {
          console.log(`🔍 Continuity check: EF_2023_city=${cityEmissionFactor2023.toFixed(8)}, EF_2023_county=${countyEmissionFactor2023.toFixed(8)}, ratio=${emissionFactorScalingFactor.toFixed(6)}`);
        }
      }
      
      // Debug emission factor for 2024-2027 to verify correct values are used
      if (year >= 2024 && year <= 2027) {
        console.log(`🔍 Emission Factor ${year}: yearEmissionFactor_county=${yearEmissionFactorCounty.toFixed(8)}, adjustedEmissionFactor=${adjustedEmissionFactor.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}, from emfacYearData=${emfacYearData?.ghg_per_mile?.toFixed(8) || 'NULL'}, avgEmissionFactor=${avgEmissionFactor.toFixed(8)}`);
      }
      
      // Calculate gradual VMT reduction percentage for this year
      // Gradual ramp: increases by targetVMTReduction% / (2045 - 2023) each year from 2024 to 2045
      // Formula: annualVMTReductionIncrease = targetVMTReduction% / (2045 - 2023)
      // For year Y: vmtReductionPercentThisYear = 0 + annualIncrease * (Y - 2023)
      const yearsTo2045 = 2045 - 2023; // Total years from 2023 to 2045 (22 years)
      const yearsElapsed = year - 2023; // Years elapsed since 2023
      const annualVMTReductionIncrease = vmtReductionPercent / yearsTo2045; // Annual increase in VMT reduction percentage
      
      // Calculate VMT reduction percentage for this year
      const vmtReductionPercentThisYear = yearsElapsed <= 0 
        ? 0 // 2023: no VMT reduction yet
        : Math.min(vmtReductionPercent, annualVMTReductionIncrease * yearsElapsed); // 2024-2045: gradual increase
      
      // Apply gradual VMT reduction from policy tool
      const reducedVmt = projectedVmt * (1 - vmtReductionPercentThisYear / 100);
      
      // Calculate EV adoption percentage for this year
      // Gradual ramp between current and target from 2023->2045, supporting both increases and decreases.
      const annualEVIncrease = (evAdoptionPercent - currentEVPercent) / yearsTo2045; // % points per year
      const evUnclamped =
        yearsElapsed <= 0 ? currentEVPercent : currentEVPercent + (annualEVIncrease * yearsElapsed);
      const evLowerBound = Math.min(currentEVPercent, evAdoptionPercent, 100);
      const evUpperBound = Math.max(currentEVPercent, evAdoptionPercent, 0);
      const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
      
      // Debug for baseline calculation (2023-2026) when evAdoptionPercent equals currentEVPercent
      if (year <= 2026 && evAdoptionPercent === currentEVPercent) {
        console.log(`🔍 Baseline Calc ${year}: evAdoptionPercent=${evAdoptionPercent}, currentEVPercent=${currentEVPercent}, annualEVIncrease=${annualEVIncrease.toFixed(6)}, evPercentThisYear=${evPercentThisYear.toFixed(2)}, yearsElapsed=${yearsElapsed}`);
      }
      
      // Calculate fleet-weighted emission factor based on EV adoption
      // IMPORTANT: For baseline BAU (evAdoptionPercent === currentEVPercent2045), use adjustedEmissionFactor directly.
      // The emission factors from EMFAC (emfac2025_county_filtered or emfac2021_county_filtered) already include
      // the baseline EV adoption that EMFAC models. We should NOT apply our own EV adoption ramp on top of it.
      // This prevents double-counting and ensures the baseline matches EMFAC's projections.
      // For 2024, adjustedEmissionFactor ensures continuity with 2023 GHG inventory value.
      let fleetEmissionFactor = adjustedEmissionFactor;
      
      // Only apply EV adoption adjustment if it differs from baseline BAU
      // For baseline BAU: evAdoptionPercent === currentEVPercent2045, so evPercentThisYear will match baseline
      // For policy scenarios: evAdoptionPercent !== currentEVPercent2045, so we apply additional EV adoption
      const isBaselineBAU = Math.abs(evAdoptionPercent - currentEVPercent) < 0.01; // Allow small floating point differences
      
      if (!isBaselineBAU && gridIntensity !== null) {
        // Calculate EV emission factor
        const evGhgPerMileTons = (gridIntensity * CONSTANTS.EV_KWH_PER_MILE) / 1000; // kg to tons
        
        // Use adjusted emission factor as base (city-specific, scaled from county-level EMFAC)
        const rawEmissionFactor = adjustedEmissionFactor;
        
        // Calculate fleet-weighted emission factor based on actual EV percentage for this year
        // Formula: fleetEF = rawEF * (1 - evPercent/100) + evEF * (evPercent/100)
        // This ensures:
        // - At baseline EV%: fleetEF = rawEmissionFactor (matches EMFAC baseline)
        // - At 100% EV: fleetEF = evGhgPerMileTons (all EVs)
        const evFraction = evPercentThisYear / 100;
        fleetEmissionFactor = rawEmissionFactor * (1 - evFraction) + evGhgPerMileTons * evFraction;
        
        // Ensure emission factor doesn't go below EV-only emissions
        fleetEmissionFactor = Math.max(evGhgPerMileTons, fleetEmissionFactor);
      }
      
      // Calculate emissions: VMT * emission factor
      // For baseline BAU: fleetEmissionFactor = yearEmissionFactor (from EMFAC, decreases over time)
      // This ensures emissions decrease as EMFAC emission factors decrease
      const lightDutyEmissions = reducedVmt * fleetEmissionFactor;
      const effectiveEmissionFactor = fleetEmissionFactor;
      
      // Debug calculation for 2023-2027 to identify bump
      if (year >= 2023 && year <= 2027) {
        const historical2023 = historicalData.find(d => d.year === 2023);
        const ldv2023 = historical2023?.lightDutyEmissions || 0;
        console.log(`🔍 Emissions Calc ${year}: reducedVmt=${reducedVmt.toFixed(0)}, fleetEmissionFactor=${fleetEmissionFactor.toFixed(8)}, lightDutyEmissions=${lightDutyEmissions.toFixed(2)}`);
        if (year === 2024) {
          // Get 2023 EMFAC emission factor for comparison
          console.log(`🔍 2024 vs 2023: 2023_emissions=${ldv2023.toFixed(2)}, 2024_emissions=${lightDutyEmissions.toFixed(2)}, change=${((lightDutyEmissions - ldv2023) / ldv2023 * 100).toFixed(2)}%`);
          console.log(`🔍 2024 VMT: baselineVmt_2023=${baselineVmt.toFixed(0)}, forecasting_ratio_2024=${emfacYearRatio?.toFixed(6) || 'NULL'}, projectedVmt_2024=${projectedVmt.toFixed(0)}`);
          console.log(`🔍 2024 EF: yearEmissionFactor_county_2024=${yearEmissionFactorCounty.toFixed(8)}, adjustedEmissionFactor_2024=${adjustedEmissionFactor.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}`);
        }
      }
      
      // Log EV adoption and VMT reduction progress for debugging (log first few years and every 5 years)
      if (year <= 2027 || year % 5 === 0 || year === 2045) {
        console.log(`Year ${year}: VMT=${projectedVmt.toFixed(0)} (reduced=${reducedVmt.toFixed(0)}), VMT Reduction=${vmtReductionPercentThisYear.toFixed(2)}% (target: ${vmtReductionPercent}%, annual increase: ${annualVMTReductionIncrease.toFixed(3)}%/year), EV%=${evPercentThisYear.toFixed(2)}% (target: ${evAdoptionPercent}%, annual increase: ${annualEVIncrease.toFixed(3)}%/year), Fleet EF=${effectiveEmissionFactor.toFixed(6)} tons/mile (adjustedEF=${adjustedEmissionFactor.toFixed(6)}), Emissions=${lightDutyEmissions.toFixed(0)} tons CO2e`);
      }
      
      // For other sources:
      // - Waste scales by county population.
      // - Natural Gas and Electricity use Buildings-page consumption (therms/kWh) + (g/kWh) intensity.
      // - Heavy Duty + Offroad: scale by population and decarbonize 1.5%/year.
      const hdvOffroadDecarbFactor = Math.pow(0.985, year - 2023);

      const pop = countyPopByYear[year] || basePop;
      const popFactor = basePop > 0 ? pop / basePop : 1;

      // Buildings: compute electricity + natural gas emissions from consumption and intensity.
      // Natural gas: 5.29 kg CO2e/therm => 0.00529 metric tons/therm
      const totalTherms = calculateTotalNaturalGasConsumption(buildingsEnergyData, year);
      const naturalGasEmissions = (totalTherms * 5.29) / 1000;

      const totalKwh = calculateTotalElectricityConsumption(buildingsEnergyData, year);
      const denom = Math.max(1, cleanElectricityYear - 2023);
      const t = clampNumber((year - 2023) / denom, 0, 1);
      const gPerKwh = g2023 * (1 - t);
      const electricityEmissions = (totalKwh * gPerKwh) / 1_000_000; // g -> metric tons
      
      projectedData.push({
        year,
        lightDutyEmissions: Math.round(lightDutyEmissions),
        heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
        naturalGasEmissions: Math.round(naturalGasEmissions),
        electricityEmissions: Math.round(electricityEmissions),
        wasteEmissions: Math.round((historical2023?.wasteEmissions || 0) * popFactor),
        offroadEmissions: Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
        totalEmissions: Math.round(lightDutyEmissions) + 
                       Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor) +
                       Math.round(naturalGasEmissions) +
                       Math.round(electricityEmissions) +
                       Math.round((historical2023?.wasteEmissions || 0) * popFactor) +
                       Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor)
      });
    }
    
    console.log('Projected emissions calculated:', projectedData);
    return projectedData;
  } catch (error) {
    console.error('Error calculating projected emissions:', error);
    return [];
  }
}

/**
 * Calculate and cache baseline projections (expensive, done once per location)
 * This includes all the expensive database queries and calculations
 */
async function calculateAndCacheBaseline(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<BaselineProjections> {
  console.log(`📊 Calculating baseline projections for ${location} using EMFAC${emfacVersion} (this may take a few seconds)...`);
  
  // Calculate historical emissions (2005-2023) using EMFAC version for LDV
    const historicalData = await calculateHistoricalEmissions(location, emfacVersion);
    
    // Fetch baseline data to get EV percentages
    // currentEVPercent2023: 2023 EV% (starting point for baseline BAU)
    // currentEVPercent: 2045 BAU EV% (target for baseline BAU, used as slider default)
    const baseline = await fetchBaselineGHGData(location, emfacVersion);
    const currentEVPercent2023 = baseline.currentEVPercent2023 || 0; // 2023 EV% (6.01% for Berkeley)
    const currentEVPercent2045 = baseline.currentEVPercent || 0; // 2045 BAU EV% (69.27% for Berkeley)
  
  // Get VMT data (includes user customizations)
  let geofips: number | null = null;
  try {
    await supabaseLocationService.initialize();
    geofips = await supabaseLocationService.getGeofips(location);
  } catch (error) {
    console.warn('Could not get geofips for VMT data:', error);
  }
  const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
  const latestVmtData = vmtData.find(d => d.year === 2023);
  const baselineVmt = latestVmtData?.vmt || 0;
  
  // Get EMFAC forecasting data
  const emfacData = await fetchEMFACForecastingData(location, emfacVersion);
  
  // Get population data
  const countyPopRaw = await fetchCountyPopulationByYear(location);
  const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);
  
  // Get buildings energy data (includes user customizations)
  const buildingsEnergyData = await getBuildingsEnergyData(location);

  // Get electricity carbon intensity series (gCO2e/kWh) for the location (Buildings page input)
  let electricityCarbonIntensityGPerKwhByYear: Record<number, number> = {};
  try {
    let geofipsForCI: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofipsForCI = await supabaseLocationService.getGeofips(location);
    } catch {
      geofipsForCI = null;
    }
    if (geofipsForCI) {
      const ci = await fetchCarbonIntensityData(String(geofipsForCI));
      const years = (ci.chartData?.labels || []) as any[];
      const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
      years.forEach((y: any, i: number) => {
        const yr = Number(y);
        const v = Number(data[i]);
        if (Number.isFinite(yr) && Number.isFinite(v) && v > 0) electricityCarbonIntensityGPerKwhByYear[yr] = v;
      });
    }
  } catch (e) {
    console.warn('Policy Tool: could not fetch carbon intensity series for baseline cache', e);
    electricityCarbonIntensityGPerKwhByYear = {};
  }
  
  // Calculate baseline projections (2024-2045) with NO policies
  // Note: 2023 is the baseline year and comes from historical data, not projections
  // IMPORTANT: For baseline BAU, we use emission factors from EMFAC directly (no EV adoption adjustment).
  // The emission factors from EMFAC already include the baseline EV adoption that EMFAC models.
  // We pass currentEVPercent2045 as both target and current to indicate "no change from baseline",
  // which will cause the code to use yearEmissionFactor directly without EV adjustments.
  console.log('🔍 calculateAndCacheBaseline: Calculating baseline BAU using EMFAC emission factors directly (no EV adjustment)');
  const baselineProjected = await calculateProjectedEmissions(
      location, 
      historicalData, 
    0, // No VMT reduction
    currentEVPercent2045, // Target EV% = 2045 BAU (69.27% for Berkeley)
      currentEVPercent2045, // Current EV% = 2045 BAU (same as target = no change = baseline)
    undefined, // No scenario (baseline only)
    emfacVersion // EMFAC version
  );
  console.log('🔍 calculateAndCacheBaseline: Baseline projected years:', baselineProjected.map(d => `${d.year}: LDV=${d.lightDutyEmissions}`).slice(0, 5));
  
  // Create hash of VMT data for cache invalidation (using hash functions from cache service)
  const { hashVMTData, hashBuildingsEnergyData } = await import('./policyBaselineCache');
  const vmtHash = hashVMTData(vmtData);
  const energyHash = hashBuildingsEnergyData(buildingsEnergyData);

  // Use historical 2023 from GHG Inventory chart directly (which uses VMT from VMT chart)
  // This ensures perfect continuity with historical data and respects user customizations
  // Projections start from 2024 using EMFAC forecasting ratios
  const historical2023 = historicalData.find(d => d.year === 2023);
  
  // Calculate emission factor scaling factor for city-specific adjustment
  // Formula: EF_city_2023 / EF_county_2023
  // This ensures continuity: 2023 uses city-specific EF, 2024+ uses county EF * scaling_factor
  let emissionFactorScalingFactor = 1.0;
  try {
    const ldv2023 = historical2023?.lightDutyEmissions || 0;
    if (ldv2023 > 0 && baselineVmt > 0) {
      // Calculate city-specific emission factor from GHG inventory: emissions / VMT
      const cityEmissionFactor2023 = ldv2023 / baselineVmt; // tons CO2 per mile
      
      // Get county-level emission factor for 2023 from EMFAC data
      const emfacInterp = buildEmfacInterpolator((emfacData || []) as EmfacForecastRow[]);
      const emfac2023Data = emfacInterp.getRow(2023) || emfacData.find(d => d.year === 2023);
      const countyEmissionFactor2023 = emfac2023Data?.ghg_per_mile;
      
      if (countyEmissionFactor2023 && countyEmissionFactor2023 > 0) {
        emissionFactorScalingFactor = cityEmissionFactor2023 / countyEmissionFactor2023;
        console.log(`🔍 Baseline Scaling Factor: ${emissionFactorScalingFactor.toFixed(6)} (city_2023=${cityEmissionFactor2023.toFixed(8)} / county_2023=${countyEmissionFactor2023.toFixed(8)})`);
      }
    }
  } catch (error) {
    console.warn('Could not calculate emission factor scaling factor for baseline:', error);
  }
  
  // Baseline projections: 2023 (from GHG Inventory) + 2024-2045 (from projections)
  // The GHG Inventory chart already uses the correct VMT data from the VMT chart
  const baselineProjectionsArray = historical2023
    ? [historical2023, ...baselineProjected]
    : baselineProjected;
  
  const baselineProjections: BaselineProjections = {
    location,
    vmtHash,
    energyHash,
    historicalData,
    baselineProjections: baselineProjectionsArray, // 2023 (historical) + 2024-2045 (projections)
    baselineVMT: baselineVmt,
      currentEVPercent: currentEVPercent2023, // Store 2023 EV% as starting point for baseline BAU
    currentEVPercent2045: currentEVPercent2045, // Store 2045 BAU EV% (slider default) for comparison
    solarCapacityKw: baseline.solarCapacityKw || 0,
    population: baseline.population || 0,
    emfacData,
    populationData: countyPopByYear,
    buildingsEnergyData,
    electricityCarbonIntensityGPerKwhByYear,
    emissionFactorScalingFactor, // Store scaling factor for use in policy transformations
    calculationDate: new Date()
  };
  
  // Cache the baseline with EMFAC version in key
  const cacheKey = `baseline:${location}:emfac${emfacVersion}`;
  setCachedBaseline(cacheKey, baselineProjections);
  
  console.log('✅ Baseline projections calculated and cached for', location);
  return baselineProjections;
}

/**
 * Apply policy transformations to cached baseline (instant, lightweight)
 * This performs only math operations - no database queries
 */
export function applyPolicyTransformations(
  baseline: BaselineProjections,
  scenario: PolicyScenario
): TimeSeriesEmissionData[] {
  const targetEVPercent = scenario.evAdoptionPercent || 0;
  const vmtReductionPercent = scenario.vmtReductionPercent || 0;
  // Policy multipliers (instant calculations)
    const hdvScale = Math.max(0, 1 - (scenario.hdvElectrificationPercent || 0) / 100);
    const targetEnergyEfficiencyPercent = Math.max(0, Math.min(40, scenario.energyEfficiencyPercent || 0));
    const targetBuildingElectrificationPercent = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
    const targetOrganicsDiversionPercent = Math.max(50, Math.min(100, (scenario as any).organicsDiversionPercent || 50));
    const cleanElectricityYear = Math.max(2025, Math.min(2045, (scenario as any).gridCleanElectricityYear ?? 2045));

  // Calculate grid intensity for EV adoption (if needed)
  const CONSTANTS = {
    BASELINE_GRID_INTENSITY: 0.2,
    ANNUAL_SOLAR_HOURS: 2000,
    SOLAR_DISPLACEMENT_FACTOR: 0.85,
    EV_KWH_PER_MILE: 0.3,
    ICE_IMPROVEMENT_FACTOR: 0.95,
  };
  
  // Get EMFAC data for emission factors
  const validEmissionFactors = baseline.emfacData.filter((d: { ghg_per_mile?: number }) => 
    d.ghg_per_mile !== undefined && d.ghg_per_mile !== null && d.ghg_per_mile > 0
  );
  const avgEmissionFactor = validEmissionFactors.length > 0 
    ? validEmissionFactors.reduce((sum: number, d: { ghg_per_mile?: number }) => 
        sum + (d.ghg_per_mile ?? 0), 0
      ) / validEmissionFactors.length
    : 0.0008;
  const emfacInterp = buildEmfacInterpolator((baseline.emfacData || []) as EmfacForecastRow[]);
  const ciByYear = baseline.electricityCarbonIntensityGPerKwhByYear || {};
  const ciFallback2023 = ciByYear[2023] || 300; // gCO2e/kWh fallback
  const baseG2023 = ciByYear[2023] || ciFallback2023;
  
  // Calculate grid intensity for EV charging using Clean Electricity Year logic
  // (linearly reduce from 2023 Buildings-page carbon intensity to 0 by cleanElectricityYear)
  const getGridIntensityForYear = (year: number): number => {
    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    return baseG2023 * (1 - t); // g CO2e/kWh
  };
  
  // TEMPORARY: Removed computeFleetEmissionFactorFromYearBase - using raw EMFAC emission factors directly
  
  // Track cumulative VMT as we apply ratios sequentially
  // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1) (from emfac2025_county_filtered)
  // So to get VMT_2024: VMT_2024 = VMT_2023 * forecasting_ratio_2024
  // To get VMT_2025: VMT_2025 = VMT_2024 * forecasting_ratio_2025
  let currentVmt = baseline.baselineVMT;
  
  // Apply transformations to each projected year
  // IMPORTANT: 2023 is the baseline year - no policies should be applied to it
  console.log('🔍 applyPolicyTransformations: Starting transformations');
  console.log('🔍 Baseline currentEVPercent:', baseline.currentEVPercent);
  console.log('🔍 Target EV percent:', targetEVPercent);
  console.log('🔍 Baseline projections years:', baseline.baselineProjections.map((d: TimeSeriesEmissionData) => d.year));
  
  const transformedProjections = baseline.baselineProjections.map((baseRow: TimeSeriesEmissionData) => {
    const year = baseRow.year;
    const yearsElapsed = year - 2023;
    
    // EV adoption ramping
    // The baseline BAU already ramps from currentEVPercent (2023, 6.01%) to currentEVPercent2045 (2045, 69.27%)
    // If target equals currentEVPercent2045 (slider at default), use baseline as-is
    // Otherwise, apply ADDITIONAL EV adoption above/below the baseline ramp
    const baselineEV2045 = baseline.currentEVPercent2045 || baseline.currentEVPercent; // 2045 BAU EV% (slider default)
    const additionalEVPercent = targetEVPercent - baselineEV2045; // Compare against 2045 BAU value, not 2023 value
    
    // Debug for 2023-2026 to understand what's happening
    if (year <= 2026) {
      console.log(`🔍 Year ${year}: yearsElapsed=${yearsElapsed}, additionalEVPercent=${additionalEVPercent.toFixed(2)}%, baseRow.lightDutyEmissions=${baseRow.lightDutyEmissions}`);
    }
    
    // Calculate EV% for this year
    // The baseline BAU ramps from currentEVPercent (2023) to currentEVPercent2045 (2045)
    // For policy scenarios, we add/subtract from the baseline ramp
    // Linear ramp: 2023 = 0 reduction, 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22 (full reduction)
    // Total reduction steps = 22 (2024-2045, excluding 2023)
    const totalReductionSteps = 2045 - 2023; // 22 steps (2024 to 2045)
    
    // Calculate ramp step: 2023 = 0, 2024 = 1, 2025 = 2, ..., 2045 = 22
    // 2023 has no reduction (baseline), reductions start in 2024
    // For 2023, yearsElapsed = 0, so rampStep = 0 (no reduction)
    // For 2024+, yearsElapsed >= 1, so rampStep = yearsElapsed (1, 2, ..., 22)
    const rampStep = year === 2023 ? 0 : Math.max(0, yearsElapsed); // 2023 = 0, 2024 = 1, ..., 2045 = 22
    
    // Calculate baseline EV% for this year (ramps from 2024 to 2045, not 2023)
    // Baseline BAU ramp: 2023 = currentEVPercent (no change), 2024 starts ramping, 2045 = currentEVPercent2045
    // For 2023, rampStep = 0, so baseline stays at currentEVPercent
    const baselineEVPercentThisYear = year === 2023
      ? baseline.currentEVPercent 
      : baseline.currentEVPercent + ((baselineEV2045 - baseline.currentEVPercent) / totalReductionSteps) * rampStep;
    
    // Calculate policy adjustment: additional EV% above/below baseline BAU
    // Reductions start in 2024 (rampStep = 1), 2023 has no reduction (rampStep = 0)
    // For 2023, rampStep = 0, so no policy adjustment is applied
    const annualEVIncrease = additionalEVPercent / totalReductionSteps;
    const evUnclamped = baselineEVPercentThisYear + (annualEVIncrease * rampStep);
    const evLowerBound = Math.min(baseline.currentEVPercent, targetEVPercent, baselineEV2045);
    const evUpperBound = Math.max(baseline.currentEVPercent, targetEVPercent, baselineEV2045);
    const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
    const additionalEVPercentThisYear = evPercentThisYear - baselineEVPercentThisYear;
    
    // Debug log for baseline year (2023) and first few years to verify smooth ramping
    if (year <= 2026) {
      if (additionalEVPercent > 0) {
        console.log(`Policy Transform ${year}: additionalEVPercent=${additionalEVPercent.toFixed(2)}%, annualIncrease=${annualEVIncrease.toFixed(3)}%, additionalThisYear=${additionalEVPercentThisYear.toFixed(3)}%, evPercentThisYear=${evPercentThisYear.toFixed(2)}%`);
      } else {
        console.log(`Policy Transform ${year}: No additional EV adoption (baseline year or default slider)`);
      }
    }
    
    // VMT reduction ramping (same logic as EV adoption, uses same rampStep)
    // Linear ramp: 2023 = 0 reduction, 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22 (full reduction)
    const annualVMTReductionIncrease = vmtReductionPercent / totalReductionSteps;
    const vmtReductionPercentThisYear = Math.min(vmtReductionPercent, annualVMTReductionIncrease * rampStep);
    
    // If no additional EV adoption and no other policy changes, return baseline row unchanged
    // This ensures slider at default behaves exactly like zero - no drop in 2024
    // Check both the total policy and the year-specific reduction to catch edge cases
    if (additionalEVPercent === 0 && vmtReductionPercent === 0 && 
        (scenario.hdvElectrificationPercent || 0) === 0 &&
        targetBuildingElectrificationPercent === 0 &&
        targetEnergyEfficiencyPercent === 0 &&
        targetOrganicsDiversionPercent === 50 &&
        (scenario.solarCapacityMultiplier || 1) === 1 &&
        // Grid lever: only "no-op" when left at default 2045
        cleanElectricityYear === 2045) {
      if (year <= 2026) {
        console.log(`✅ Year ${year}: Returning baseline row unchanged (no policy changes)`);
      }
      return baseRow;
    }
    
    // Debug: If we're applying changes, log what's happening
    if (year <= 2026) {
      console.log(`⚠️ Year ${year}: Applying policy changes (additionalEVPercent=${additionalEVPercent.toFixed(2)}%, vmtReductionPercent=${vmtReductionPercent.toFixed(2)}%)`);
    }
    
    // Find EMFAC data for this year (for emission factor and VMT)
    const emfacYearData = emfacInterp.getRow(year) || baseline.emfacData.find((d: { year: number }) => d.year === year);
    const yearEmissionFactorCounty = emfacYearData?.ghg_per_mile || avgEmissionFactor;
    // Apply scaling factor to convert county-level emission factor to city-specific
    // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
    const emissionFactorScalingFactor = baseline.emissionFactorScalingFactor || 1.0;
    const adjustedEmissionFactor = yearEmissionFactorCounty * emissionFactorScalingFactor;
    // Use the adjusted (city-specific) emission factor as the base for EV policy calculations
    const emissionFactorForEvPolicy = adjustedEmissionFactor;
    
    // For projections (2024+), ALWAYS use forecasting ratios from emfac2025_county_filtered
    // Historical data is only to 2023. Everything after 2023 is a projection using ratios.
    // Never use actual VMT values for projections - they may have discontinuities.
    // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
    // Applied as: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
    const emfacYearRatio = emfacYearData?.forecasting_ratio;
    let baselineVmt: number;
    
    if (year === 2023) {
      // 2023: use baseline VMT directly (historical data)
      baselineVmt = baseline.baselineVMT;
    } else if (emfacYearRatio && emfacYearRatio > 0) {
      // For 2024+: Always use forecasting ratio for projections
      baselineVmt = currentVmt * emfacYearRatio;
      currentVmt = baselineVmt; // Update for next iteration
    } else {
      // No forecasting ratio available, keep previous VMT (should not happen for 2024+)
      console.warn(`⚠️ No forecasting ratio available for year ${year}, keeping previous VMT`);
      baselineVmt = currentVmt;
    }
    
    const reducedVmt = baselineVmt * (1 - vmtReductionPercentThisYear / 100);
    
    // Calculate LDV emissions with EV adoption policy
    // Scale linearly from baseline (currentEVPercent) to zero emissions (100% EV)
    const emissionFactor = emissionFactorForEvPolicy; // Adjusted (city-specific) emission factor
    let lightDutyEmissions: number;
    
    // 2023 should always use the GHG inventory value, regardless of policy settings
    // Reductions start in 2024 (1/22 of 2045 reduction), not 2023
    if (year === 2023) {
      lightDutyEmissions = baseRow.lightDutyEmissions || 0;
    } else if (additionalEVPercent === 0 && vmtReductionPercentThisYear === 0) {
      // No additional EV adoption and no VMT reduction - use baseline LDV emissions exactly
      lightDutyEmissions = baseRow.lightDutyEmissions || 0;
    } else if (additionalEVPercent === 0) {
      // No additional EV adoption but VMT reduction - adjust only for VMT reduction
      const baselineVmtRatio = baselineVmt > 0 ? reducedVmt / baselineVmt : 1;
      lightDutyEmissions = (baseRow.lightDutyEmissions || 0) * baselineVmtRatio;
    } else {
      // EV adoption differs from baseline - calculate fleet-weighted emission factor
      // IMPORTANT: We use adjustedEmissionFactor (city-specific, scaled from county-level EMFAC)
      // This emission factor already accounts for the baseline EV adoption in EMFAC's model
      // For policy scenarios where EV% differs from baseline, we need to adjust the emission factor
      
      // Use adjusted emission factor (city-specific, scaled) as the base
      // This represents the fleet-average emission factor from EMFAC (adjusted for city-specific scaling), which already includes
      // the baseline EV adoption in the model. For policy calculations, we adjust based on
      // the difference between target EV% and baseline EV%
      const rawEmissionFactor = emissionFactor; // Adjusted (city-specific) emission factor
      
      // Calculate grid intensity for EV charging
      const gridIntensityGPerKwh = getGridIntensityForYear(year);
      const gridIntensityKgPerKwh = gridIntensityGPerKwh / 1000;
      const evGhgPerMileTons = (gridIntensityKgPerKwh * CONSTANTS.EV_KWH_PER_MILE) / 1000;
      
      // Calculate fleet-weighted emission factor with LINEAR reduction
      // The reduction should be linear: 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22
      // First, calculate what the baseline BAU EV% would be this year (ramps from 2023 to 2045)
      const baselineEV2023 = baseline.currentEVPercent; // 2023 baseline EV% (starting point)
      const baselineEV2045 = baseline.currentEVPercent2045 || baseline.currentEVPercent; // 2045 BAU EV%
      const targetEV2045 = targetEVPercent; // Target EV% in 2045 (from slider)
      
      // Calculate baseline BAU EV% for this year (ramps linearly from 2023 to 2045)
      const baselineEVThisYear = year === 2023
        ? baselineEV2023
        : baselineEV2023 + ((baselineEV2045 - baselineEV2023) / totalReductionSteps) * rampStep;
      
      // Calculate the total EV% increase needed from baseline BAU to target in 2045
      const totalEVIncreaseNeeded = targetEV2045 - baselineEV2045;
      
      // Apply linear ramp: rampStep / 22 of the total increase ON TOP OF baseline BAU
      // 2024: baseline BAU + (1/22) * total increase
      // 2025: baseline BAU + (2/22) * total increase
      // etc.
      const evPercentThisYearLinear = baselineEVThisYear + (totalEVIncreaseNeeded * rampStep / totalReductionSteps);
      
      // Clamp to valid range
      const evPercentFinal = Math.max(0, Math.min(100, evPercentThisYearLinear));
      
      // Calculate fleet-weighted emission factor based on actual EV percentage
      // The rawEmissionFactor represents the baseline fleet average at baselineEVThisYear
      // We need to calculate what the emission factor would be at evPercentFinal
      // 
      // The baseline emission factor at baselineEVThisYear is:
      // rawEmissionFactor = ICE_EF * (1 - baselineEVThisYear/100) + EV_EF * (baselineEVThisYear/100)
      // 
      // To get ICE_EF, we solve: ICE_EF = (rawEmissionFactor - EV_EF * baselineEVThisYear/100) / (1 - baselineEVThisYear/100)
      // Then calculate fleet EF at target: fleetEF = ICE_EF * (1 - evPercentFinal/100) + EV_EF * (evPercentFinal/100)
      
      const baselineEVFraction = baselineEVThisYear / 100;
      const targetEVFraction = evPercentFinal / 100;
      
      // Extract ICE emission factor from baseline fleet average
      // rawEmissionFactor = ICE_EF * (1 - baselineEVFraction) + EV_EF * baselineEVFraction
      // Solving for ICE_EF:
      const denominator = 1 - baselineEVFraction;
      const iceGhgPerMile = denominator > 0.001 // Avoid division by zero
        ? (rawEmissionFactor - evGhgPerMileTons * baselineEVFraction) / denominator
        : rawEmissionFactor; // Fallback if baseline is already 100% EV
      
      // Calculate fleet-weighted emission factor at target EV percentage
      const icePercent = 1 - targetEVFraction;
      let fleetEmissionFactor = (icePercent * iceGhgPerMile) + (targetEVFraction * evGhgPerMileTons);
      
      // Ensure emission factor doesn't go below EV-only emissions
      const finalEmissionFactor = Math.max(evGhgPerMileTons, fleetEmissionFactor);
      
      // Calculate emissions: VMT * fleet emission factor
      lightDutyEmissions = reducedVmt * finalEmissionFactor;
      
      // Debug log for first few years
      if (year <= 2026) {
        console.log(`🔍 EV Calc ${year}: baselineEVThisYear=${baselineEVThisYear.toFixed(2)}%, baselineEV2045=${baselineEV2045.toFixed(2)}%, targetEV2045=${targetEV2045.toFixed(2)}%, totalIncrease=${totalEVIncreaseNeeded.toFixed(2)}%, rampStep=${rampStep}, evPercentFinal=${evPercentFinal.toFixed(2)}%, rawEF=${rawEmissionFactor.toFixed(8)}, finalEF=${finalEmissionFactor.toFixed(8)}, Emissions=${lightDutyEmissions.toFixed(0)}`);
      }
    }
    
    // Apply population scaling and decarbonization factors
    // Buildings policy ramps (linear from 2023 -> 2045)
    // 2023 = 0, 2024 = 1/22, ..., 2045 = 22/22 (full implementation)
    const totalSteps = 2045 - 2023; // 22 steps (2024-2045, excluding 2023)
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const energyEfficiencyThisYear = targetEnergyEfficiencyPercent * ramp;
    const buildingElectrificationThisYear = targetBuildingElectrificationPercent * ramp;
    const organicsDiversionThisYear = targetOrganicsDiversionPercent * ramp;
    // Buildings:
    // - Project electricity as kWh * (g/kWh)
    // - Energy efficiency reduces kWh and therms linearly to 2045
    // - Building electrification replaces therms with electricity kWh:
    //     1 therm = 29.3 kWh thermal, heat pump COP = 3.5
    //     => added_kWh = therms_replaced * (29.3/3.5)
    const COP = 3.5;
    const KWH_PER_THERM = 29.3;

    const baseTherms = calculateTotalNaturalGasConsumption(baseline.buildingsEnergyData, year);
    const baseKwh = calculateTotalElectricityConsumption(baseline.buildingsEnergyData, year);

    const effFrac = clampNumber(energyEfficiencyThisYear / 100, 0, 1);
    const elecFrac = clampNumber(buildingElectrificationThisYear / 100, 0, 1);

    // Apply efficiency first (reduces demand), then electrify a share of remaining therms.
    const thermsAfterEff = baseTherms * (1 - effFrac);
    const thermsReplaced = thermsAfterEff * elecFrac;
    const thermsRemaining = Math.max(0, thermsAfterEff - thermsReplaced);

    const kwhAfterEff = baseKwh * (1 - effFrac);
    const addedKwh = thermsReplaced * (KWH_PER_THERM / COP);
    const totalKwh = kwhAfterEff + addedKwh;

    // Grid decarbonization modeled as the year electricity reaches zero emissions.
    // Linearly reduce from the 2023 Buildings-page value to 0 by `cleanElectricityYear`.
    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    const gPerKwhPolicy = baseG2023 * (1 - t);

    // If we're only changing the grid clean year (no efficiency, no building electrification),
    // keep natural gas exactly at baseline to avoid small numeric drift showing as "NG reductions".
    const naturalGasEmissionsPolicy =
      effFrac === 0 && elecFrac === 0
        ? (baseRow.naturalGasEmissions || 0)
        : (thermsRemaining * 5.29) / 1000; // metric tons
    const electricityEmissionsPolicy = (totalKwh * gPerKwhPolicy) / 1_000_000; // g -> metric tons
    
      // Apply organics diversion reduction to waste emissions
      const organicsDiversionFrac = clampNumber(organicsDiversionThisYear / 100, 0, 1);
      const wasteEmissionsPolicy = (baseRow.wasteEmissions || 0) * (1 - organicsDiversionFrac);
      
      return {
      year,
      lightDutyEmissions: Math.round(lightDutyEmissions),
        heavyDutyEmissions: Math.round((baseRow.heavyDutyEmissions || 0) * hdvScale),
        naturalGasEmissions: Math.round(naturalGasEmissionsPolicy),
        electricityEmissions: Math.round(electricityEmissionsPolicy),
        wasteEmissions: Math.round(wasteEmissionsPolicy),
        offroadEmissions: Math.round((baseRow.offroadEmissions || 0)),
      totalEmissions: Math.round(lightDutyEmissions +
          (baseRow.heavyDutyEmissions || 0) * hdvScale +
          naturalGasEmissionsPolicy +
          electricityEmissionsPolicy +
          wasteEmissionsPolicy +
          (baseRow.offroadEmissions || 0))
      } as TimeSeriesEmissionData;
    });
  
  return transformedProjections;
}

/**
 * Main function to generate time series data for policy impact tool
 * Uses baseline cache for instant policy transformations
 */
export async function generatePolicyTimeSeriesData(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025'
): Promise<PolicyTimeSeriesResult> {
  try {
    console.log(`Generating policy time series data for ${location} using EMFAC${emfacVersion} with scenario:`, scenario);
    
    // Create cache key that includes EMFAC version (must match format in calculateAndCacheBaseline)
    const cacheKey = `baseline:${location}:emfac${emfacVersion}`;
    
    // First, try to get cached baseline (synchronous check - instant)
    let baseline = getCachedBaseline(cacheKey);
    
    if (!baseline) {
      // No cached baseline - calculate it (expensive, one-time)
      // Use lock mechanism to prevent concurrent calculations
      console.log(`📊 No cached baseline found for EMFAC${emfacVersion}, calculating...`);
      baseline = await getOrCreateCalculationPromise(cacheKey, () => calculateAndCacheBaseline(location, emfacVersion));
    } else {
      // Use cached baseline immediately (instant)
      // Note: We skip validation here to avoid blocking. Validation happens
      // when data actually changes (via clearBaselineCache hooks in vmtDataService/buildingsEnergyDataService)
      console.log('⚡ Using cached baseline projections (instant)');
    }
    
    // Apply policy transformations to cached baseline (instant, synchronous)
    console.log('⚡ Applying policy transformations (instant)...');
    const transformedProjections = applyPolicyTransformations(baseline, scenario);
    
    // Combine historical (2005-2022) with 2023 (baseline year from historical) and projections (2024-2045)
    // 2023 is the baseline year - it comes from historical data, not projections
    // Baseline projections start at 2024 (no policies)
    // Transformed projections start at 2024 (with policies applied)
    const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
    const historical2023 = baseline.historicalData.find((d: TimeSeriesEmissionData) => d.year === 2023);
    
    // Baseline data: historical (2005-2022) + 2023 (baseline) + baseline projections (2024-2045)
    const baselineData = historical2023 
      ? [...historicalUpTo2022, historical2023, ...baseline.baselineProjections]
      : [...historicalUpTo2022, ...baseline.baselineProjections];
    
    // Projected with policies: historical (2005-2022) + transformed projections (2023-2045)
    // transformedProjections already includes 2023 (unchanged baseline) + 2024-2045 (with policies)
    const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
    
    return {
      baselineData,
      projectedData: projectedWithPolicies,
      location
    };
  } catch (error) {
    console.error('Error generating policy time series data:', error);
    throw error;
  }
}
